import {
  useMutation,
  useQueryClient,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_SUBSCRIPTIONS,
  ADMIN_REFUNDABLE_PAYMENT,
  ADMIN_SUBSCRIPTION_ANALYTICS,
  ADMIN_SUBSCRIPTION_TREND,
} from "@/lib/graphql/queries/subscriptions";
import {
  ADMIN_REFUND_SUBSCRIPTION,
  ADMIN_UPGRADE_SUBSCRIPTION,
  ADMIN_GRANT_CREDITS,
  ADMIN_EXTEND_BILLED_SUBSCRIPTION,
} from "@/lib/graphql/mutations/subscriptions";
import {
  AdminRefundMode,
  type AdminSubscriptionFilters,
  type AdminSubscriptionItem,
  type AdminSubscriptionAnalytics,
  type SubscriptionTrendPoint,
  type AdminRefundInput,
  type AdminRefundablePayment,
  type AdminUpgradeInput,
  type AdminGrantCreditsInput,
  type AdminExtendBilledSubscriptionInput,
} from "@/types/subscription";
import { SubscriptionCycle } from "@/types/enums";
import type { PaginationInput, PaginationMeta } from "@/types/common";
import { toast } from "sonner";

function formatUpgradeDetail(input: AdminUpgradeInput): string {
  if (input.subscriptionCycle) {
    return `(${input.subscriptionCycle === SubscriptionCycle.MONTHLY ? "월간" : "연간"} 결제)`;
  }
  if (input.endDate) {
    return `(종료: ${new Date(input.endDate).toLocaleDateString("ko-KR")})`;
  }
  if (input.durationDays) {
    return `(${input.durationDays}일)`;
  }
  return "";
}

export const subscriptionKeys = {
  all: ["subscriptions"] as const,
  lists: () => [...subscriptionKeys.all, "list"] as const,
  list: (filters: AdminSubscriptionFilters & PaginationInput) =>
    [...subscriptionKeys.lists(), filters] as const,
  analytics: () => [...subscriptionKeys.all, "analytics"] as const,
  trend: (days: number) => [...subscriptionKeys.all, "trend", days] as const,
  refundablePayment: (userId: string) =>
    [...subscriptionKeys.all, "refundablePayment", userId] as const,
};

export function subscriptionAnalyticsQueryOptions() {
  return queryOptions({
    queryKey: subscriptionKeys.analytics(),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminSubscriptionAnalytics: AdminSubscriptionAnalytics;
      }>(ADMIN_SUBSCRIPTION_ANALYTICS);
      return data.adminSubscriptionAnalytics;
    },
    staleTime: 60_000,
  });
}

export function subscriptionTrendQueryOptions(days: number) {
  return queryOptions({
    queryKey: subscriptionKeys.trend(days),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminSubscriptionTrend: SubscriptionTrendPoint[];
      }>(ADMIN_SUBSCRIPTION_TREND, { days });
      return data.adminSubscriptionTrend;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 60_000,
  });
}

interface AdminSubscriptionsResponse {
  items: AdminSubscriptionItem[];
  meta: PaginationMeta;
}

export function subscriptionQueryOptions(
  filters: AdminSubscriptionFilters,
  pagination: PaginationInput
) {
  return queryOptions({
    queryKey: subscriptionKeys.list({ ...filters, ...pagination }),
    queryFn: async () => {
      const input = {
        page: pagination.page,
        limit: pagination.limit,
        searchQuery: filters.searchQuery || undefined,
        plan: filters.plan || undefined,
        status: filters.status || undefined,
        platform: filters.platform || undefined,
        platformIsNull: filters.platformIsNull || undefined,
        subscriptionCycle: filters.subscriptionCycle || undefined,
      };

      const data = await authenticatedRequest<{
        adminSubscriptions: AdminSubscriptionsResponse;
      }>(ADMIN_SUBSCRIPTIONS, { input });

      return data.adminSubscriptions;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30000,
  });
}

export function useRefundablePayment(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: subscriptionKeys.refundablePayment(userId),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminRefundablePayment: AdminRefundablePayment;
      }>(ADMIN_REFUNDABLE_PAYMENT, { userId });
      return data.adminRefundablePayment;
    },
    enabled: enabled && !!userId,
    staleTime: 10_000,
  });
}

export function useRefundSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AdminRefundInput) => {
      const data = await authenticatedRequest<{
        adminRefundSubscription: { success: boolean; message?: string };
      }>(ADMIN_REFUND_SUBSCRIPTION, { input });
      return data.adminRefundSubscription;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      const description =
        variables.mode === AdminRefundMode.FREE_ONLY
          ? "구독을 FREE로 전환했습니다 (결제 환불은 외부에서 처리하세요)"
          : `${(variables.amount ?? 0).toLocaleString()}원 토스 환불 + 구독 FREE 전환`;
      toast.success("환불 처리가 완료되었습니다", { description });
    },
    onError: (error: Error) => {
      toast.error("환불 처리 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useUpgradeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AdminUpgradeInput) => {
      const data = await authenticatedRequest<{
        adminUpgradeSubscription: { success: boolean; message?: string };
      }>(ADMIN_UPGRADE_SUBSCRIPTION, { input });
      return data.adminUpgradeSubscription;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });

      toast.success("구독 업그레이드 완료", {
        description: `${variables.plan} 플랜으로 변경되었습니다. ${formatUpgradeDetail(variables)}`,
      });
    },
    onError: (error: Error) => {
      toast.error("업그레이드 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useGrantCredits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AdminGrantCreditsInput) => {
      const data = await authenticatedRequest<{
        adminGrantCredits: { success: boolean; message?: string };
      }>(ADMIN_GRANT_CREDITS, { input });
      return data.adminGrantCredits;
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      toast.success("크레딧 지급 완료", {
        description:
          response.message ??
          `구매 크레딧 ${variables.amount.toLocaleString()} 지급되었습니다.`,
      });
    },
    onError: (error: Error) => {
      toast.error("크레딧 지급 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useExtendBilledSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AdminExtendBilledSubscriptionInput) => {
      const data = await authenticatedRequest<{
        adminExtendBilledSubscription: { success: boolean; message?: string };
      }>(ADMIN_EXTEND_BILLED_SUBSCRIPTION, { input });
      return data.adminExtendBilledSubscription;
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      toast.success("다음 결제일 연장 완료", {
        description:
          response.message ?? `${variables.months}개월 연장되었습니다.`,
      });
    },
    onError: (error: Error) => {
      toast.error("연장 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useExtendSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AdminUpgradeInput) => {
      const data = await authenticatedRequest<{
        adminUpgradeSubscription: { success: boolean; message?: string };
      }>(ADMIN_UPGRADE_SUBSCRIPTION, { input });
      return data.adminUpgradeSubscription;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });

      const durationText = variables.endDate
        ? `(종료: ${new Date(variables.endDate).toLocaleDateString("ko-KR")})`
        : variables.durationDays
          ? `(${variables.durationDays}일)`
          : "";

      toast.success("구독 연장 완료", {
        description: `${variables.plan} 플랜이 연장되었습니다. ${durationText}`,
      });
    },
    onError: (error: Error) => {
      toast.error("연장 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}
